import Predicates from 'constants/predicates';
import API from 'constants/api';
import { convertViewToFormat } from 'helpers/dataHelpers';

/** 
 * Used to check whether an archivedObject or a resource is unique.
 * Only such resources are considered that are
 * archivedObject instances. 
 * 
 * Uniqueness of an object is defined by certain fields (such as 
 * forenames and surnameOrName fields for Actor record type.)
 * Those fields are compared against all existing records that share
 * object type. If object is not unique user is prompted whether 
 * current action should be proceeded or abandonded.
*/

/**
 * 
 * @param {*} archivedObjectFormat
 * @param {*} fields
 * @param {*} archivedObjectUri 
 * @param {*} message if records is not unique, user is asked (and the @message is shown) whether we should proceed
 * @returns 
 */

export const checkUniquenessAndPromptUser = (archivedObjectFormat, fields, oldFields, archivedObjectUri, message) => {
  return checkUniqueness(archivedObjectFormat, fields, oldFields, archivedObjectUri)
    .then(res => {
      const { isUnique } = res;
      return promptUserIfDuplicateReturnAnswer(isUnique, message);
    });
};

/**
 * 
 * @param {*} archivedObjectFormat 
 * @param {*} fields 
 * @param {*} archivedObjectUri 
 * @returns 
 */
const checkUniqueness = (archivedObjectFormat, fields, oldFields, archivedObjectUri) => {
  const urlParams = getUriParameters(archivedObjectFormat, fields, archivedObjectUri);
  const isUpdated = isValidatedFieldsUpdated(archivedObjectFormat, fields, oldFields);
  if (!urlParams || !isUpdated) {
    const r = Promise.resolve( {isUnique: true });
    return r;
  }
  return fetch(`${API.IDENTIFIER}/archivedObject/isUnique?${urlParams.toString()}`)
    .then(res => res.json())
    .catch(e => console.log(e));
};

/**
 * 
 * @param {*} isUnique 
 * @param {*} message 
 * @returns 
 */
const promptUserIfDuplicateReturnAnswer = ( isUnique, message) => {
  let continueSaving = true;
  if (!isUnique) {
    continueSaving = window.confirm(message);
  }
  return continueSaving;
};

/**
 * 
 * @param {*} archivedObjectFormat 
 * @param {*} newFields 
 * @param {*} oldFields 
 * @returns checks whether the fields whose uniqueness is being checked have changed
 */
export const isValidatedFieldsUpdated = (archivedObjectFormat, newFields, oldFields) => {
  let isChanged = false;
  if (archivedObjectFormat == Predicates.SPECTRUM.ACTOR_FORMAT) {
    const oldForename = oldFields[Predicates.SPECTRUM.FORENAMES] ? oldFields[Predicates.SPECTRUM.FORENAMES][0] : '';
    const oldSurname = oldFields[Predicates.SPECTRUM.SURNAME] ? oldFields[Predicates.SPECTRUM.SURNAME][0] : '';
    if (newFields[Predicates.SPECTRUM.FORENAMES]?.values[0] !== oldForename) {
      isChanged = true;
    } else if (newFields[Predicates.SPECTRUM.SURNAME]?.values[0] !== oldSurname) {
      isChanged = true;
    }
  } else if (archivedObjectFormat == Predicates.SPECTRUM.LOCATION_INFORMATION_FORMAT) {
    const oldLocationReference = oldFields[Predicates.SPECTRUM.LOCATION_REFERENCE] ? oldFields[Predicates.SPECTRUM.LOCATION_REFERENCE][0] : '';
    if (newFields[Predicates.SPECTRUM.LOCATION_REFERENCE]?.values[0] !== oldLocationReference) {
      isChanged = true;
    }
  } else if (archivedObjectFormat == Predicates.SPECTRUM.CE.PROPERTY_FORMAT) {
    const oldLocationReference = oldFields[Predicates.SPECTRUM.CE.PROPERTY_ID] ? oldFields[Predicates.SPECTRUM.CE.PROPERTY_ID][0] : '';
    if (newFields[Predicates.SPECTRUM.CE.PROPERTY_ID]?.values[0] !== oldLocationReference) {
      isChanged = true;
    }
  } else if (archivedObjectFormat == Predicates.SPECTRUM.CE.BUILDING_FORMAT) {
    const oldLocationReference = oldFields[Predicates.SPECTRUM.CE.BUILDING_ID] ? oldFields[Predicates.SPECTRUM.CE.BUILDING_ID][0] : '';
    if (newFields[Predicates.SPECTRUM.CE.BUILDING_ID]?.values[0] !== oldLocationReference) {
      isChanged = true;
    }
  }
  return isChanged;
};

/**
 * 
 * @param {*} archivedObjectFormat 
 * @param {*} fields 
 * @param {*} archivedObjectUri 
 * @returns 
 */
const getUriParameters = (archivedObjectFormat, fields, archivedObjectUri) => {
  const urlParams = new URLSearchParams({});
  if (archivedObjectFormat == Predicates.SPECTRUM.ACTOR_FORMAT) {
    if (fields[Predicates.SPECTRUM.FORENAMES]?.values[0]) {
      urlParams.append('forenames', fields[Predicates.SPECTRUM.FORENAMES]?.values[0]);
    }
    if (fields[Predicates.SPECTRUM.SURNAME]?.values[0])  {
      urlParams.append('surname', fields[Predicates.SPECTRUM.SURNAME]?.values[0]);
    }
  } else if (archivedObjectFormat == Predicates.SPECTRUM.LOCATION_INFORMATION_FORMAT) {
    if (fields[Predicates.SPECTRUM.LOCATION_REFERENCE]?.values[0]) {
      urlParams.append('locationReference', fields[Predicates.SPECTRUM.LOCATION_REFERENCE].values[0]);
    }
  } else if (archivedObjectFormat == Predicates.SPECTRUM.CE.PROPERTY_FORMAT) {
    if (fields[Predicates.SPECTRUM.CE.PROPERTY_ID]?.values[0]) {
      urlParams.append('propertyId', fields[Predicates.SPECTRUM.CE.PROPERTY_ID].values[0]);
    }
  } else if (archivedObjectFormat == Predicates.SPECTRUM.CE.BUILDING_FORMAT) {
    if (fields[Predicates.SPECTRUM.CE.BUILDING_ID]?.values[0]) {
      urlParams.append('buildingId', fields[Predicates.SPECTRUM.CE.BUILDING_ID].values[0]);
    }
  } 
  if (Array.from(urlParams.values()).length === 0) {
    return null;
  }
  urlParams.append('archivedObjectFormat', archivedObjectFormat);
  urlParams.append('archivedObjectUri', archivedObjectUri);
  return urlParams;
};

/**
 * 
 * @param {*} object either object.view or object.format should contain data on which type of record we are currently handling 
 * @param {*} LS LocaceContext used to localize the message
 * @returns 
 */
export const getUniquenessMessage = (object, LS) => {
  let fields = '';
  var view = object.view ? object.view : '';
  var object1;
  var object2;
  const archivedObjectFormat = object.format ? object.format : convertViewToFormat(view);
  if (archivedObjectFormat === Predicates.SPECTRUM.ACTOR_FORMAT) {
    object1 = LS.messages.uniquenessValidation.object.Actor1;
    object2 = LS.messages.uniquenessValidation.object.Actor2;
    fields = LS.messages.uniquenessValidation.fields.Actor;
  } else if (archivedObjectFormat === Predicates.SPECTRUM.LOCATION_INFORMATION_FORMAT) {
    object1 = LS.messages.uniquenessValidation.object.LocationInformation1;
    object2 = LS.messages.uniquenessValidation.object.LocationInformation2;
    fields = LS.messages.uniquenessValidation.fields.LocationInformation;
  } else if (archivedObjectFormat === Predicates.SPECTRUM.CE.PROPERTY_FORMAT) {
    object1 = LS.messages.uniquenessValidation.object.Property1;
    object2 = LS.messages.uniquenessValidation.object.Property2;
    fields = LS.messages.uniquenessValidation.fields.Property;
  } else if (archivedObjectFormat === Predicates.SPECTRUM.CE.BUILDING_FORMAT) {
    object1 = LS.messages.uniquenessValidation.object.Building1;
    object2 = LS.messages.uniquenessValidation.object.Building2;
    fields = LS.messages.uniquenessValidation.fields.Building;
  }
  const messageTemplate = LS.messages.uniquenessValidation.template;
  const message = messageTemplate.replace('${Placeholder-object1}', object1)
    .replace('${Placeholder-object2}', object2)
    .replace('${Placeholder-fields}', fields);

  return message;
};
